import * as React from 'react';
import { createPortal } from 'react-dom';
import type { ChangeEvent, ChangeEventHandler, Ref } from 'react';
import {
  CSS_EDITING_SCOPE_CLASS,
  CSS_EDITING_SCOPE_CONTAINER_ID,
  formatClassNames,
  keyCodes,
} from '@wix/editor-elements-common-utils';
import { useResizeObserver } from '@wix/thunderbolt-elements/src/providers/useResizeObserver';
import { usePortalPopper } from '@wix/thunderbolt-elements/src/providers/usePortalPopper';
import ComboBoxInputListModal from '@wix/thunderbolt-elements/src/components/ComboBoxInputListModal/viewer/ComboBoxInputListModal';
import { useClickOutside } from '@wix/thunderbolt-elements/src/providers/useClickOutside/useClickOutside';
import type { CustomPopperProps } from '@wix/thunderbolt-elements/src/providers/usePopper/usePopper';
import { useExpandableListKeyDown } from '@wix/thunderbolt-elements/src/providers/useExpandableListKeyDown/useExpandableListKeyDown';
import { calculateElemWidth, filterOptionsByQuery, noop } from '../../utils';
import NativeSelect from '../NativeSelect/NativeSelect';
import type { ICustomSelectProps } from '../../ComboBoxInput.types';
import semanticClassNames from '../../ComboBoxInput.semanticClassNames';
import NativeInput from '../NativeInput/NativeInput';

const popperConfig: CustomPopperProps = {
  strategy: 'fixed',
  placement: 'bottom-start',
  modifiers: [
    {
      name: 'flip',
      options: {
        boundary: 'clippingParents',
        fallbackPlacements: ['top-start', 'bottom-start'],
        allowedAutoPlacements: ['top-start', 'bottom-start'],
      },
    },
    {
      name: 'computeStyles',
      options: {
        roundOffsets: false,
      },
    },
  ],
};

const CustomSelect: React.ForwardRefRenderFunction<
  HTMLSelectElement | HTMLInputElement,
  ICustomSelectProps
> = (props, ref) => {
  const {
    className,
    styles,
    id,
    placeholder,
    onFocus,
    onSelectedOptionChange = noop,
    onBlur,
    disabled,
    required,
    value,
    options,
    setDesignableListElem,
    forceOpenDesignableList,
    onClick,
    ariaAttributes,
    isPopupPage,
    autocomplete,
    filteredOptions = [],
    setFilteredOptions,
    resetFilteredOptions,
    onFilterQueryChange = noop,
    onOpenedChange = noop,
    opened = false,
    filterQuery,
    isValid,
    shouldShowValidityIndication,
    portalClassName,
    scopedClassName,
    deviceType,
    lang,
    isComboBoxSelectionFeedbackExperimentOpen,
  } = props;

  const [designableListWidth, setDesignableListWidth] = React.useState(200);
  const closeListKeyCodes = autocomplete
    ? [keyCodes.enter, keyCodes.escape, keyCodes.tab]
    : [keyCodes.enter, keyCodes.escape, keyCodes.space, keyCodes.tab];
  const { hoveredOptionIndex, setHoveredOptionIndex, onKeyDown } =
    useExpandableListKeyDown({
      isListOpen: opened,
      openListFn: () => onOpenedChange(true),
      closeListFn: () => {
        onOpenedChange(false);
        resetFilteredOptions();
      },
      initialHoveredOptionIndex: placeholder ? -1 : 0,
      openListKeyCodes: [keyCodes.space, keyCodes.enter],
      closeListKeyCodes,
      listLength: filteredOptions.length,
    });

  const {
    ref: designableListSourceElem,
    setRef: setDesignableListSourceElem,
    popper: designableListTargetElem,
    setPopper: setDesignableListTargetElem,
    styles: popperStyles,
    attributes: popperAttributes,
    poppersWrapper: portalPoppersWrapper,
    mountPortalPoppersWrapper,
    unMountPortalPoppersWrapper,
  } = usePortalPopper<HTMLDivElement>({
    id,
    popperOptions: popperConfig,
    className: `${scopedClassName} ${CSS_EDITING_SCOPE_CLASS}`,
    containerId: CSS_EDITING_SCOPE_CONTAINER_ID,
  });

  const shouldOpenDesignableList = React.useMemo(
    () => opened || forceOpenDesignableList,
    [opened, forceOpenDesignableList],
  );
  React.useEffect(() => {
    if (shouldOpenDesignableList) {
      mountPortalPoppersWrapper();
    } else {
      unMountPortalPoppersWrapper();
    }
  }, [
    shouldOpenDesignableList,
    mountPortalPoppersWrapper,
    unMountPortalPoppersWrapper,
  ]);

  // Keep a mutable ref so the scroll handler always sees the latest portal
  // element without needing to re-register the listener on every render.
  const designableListTargetElemRef = React.useRef(designableListTargetElem);
  designableListTargetElemRef.current = designableListTargetElem;

  // Match the legacy imperative behavior: react to actual open/close
  // transitions only. Skipping the initial render keeps static renders with
  // `opened=true` (visual tests, SSR snapshots, panel previews) free of
  // layout side-effects from the scroll blocker.
  const previousOpenedRef = React.useRef(opened);
  React.useEffect(() => {
    const previousOpened = previousOpenedRef.current;
    previousOpenedRef.current = opened;

    if (previousOpened === opened || !opened) {
      return;
    }

    const preventPageScroll = (e: WheelEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!designableListTargetElemRef.current?.contains(target)) {
        e.preventDefault();
      }
    };

    document.addEventListener('wheel', preventPageScroll, { passive: false });
    document.addEventListener('touchmove', preventPageScroll, {
      passive: false,
    });

    return () => {
      document.removeEventListener('wheel', preventPageScroll);
      document.removeEventListener('touchmove', preventPageScroll);
    };
  }, [opened]);

  useClickOutside([designableListSourceElem, designableListTargetElem], () =>
    onOpenedChange(false),
  );

  const updateDesignableListWidth = React.useCallback(() => {
    if (designableListSourceElem) {
      const currentListWidth = calculateElemWidth(designableListSourceElem);

      if (currentListWidth !== designableListWidth) {
        setDesignableListWidth(currentListWidth);
      }
    }
  }, [designableListSourceElem, designableListWidth]);

  const toggleDesignableList = () => {
    updateDesignableListWidth();

    const selectRef =
      ref as React.MutableRefObject<HTMLSelectElement | null> | null;

    if (!opened) {
      selectRef?.current?.focus();
    }

    onOpenedChange(!opened);
  };

  const handleNativeSelectOnMouseDown: React.MouseEventHandler<
    HTMLSelectElement
  > = e => {
    e.preventDefault();
    toggleDesignableList();
  };

  const handleSearchNavigationWithKey = (
    key: string,
    hoveredOptionIdx: number,
    keyCode: any,
  ) => {
    const searchNavigationBlacklist = [
      keyCodes.enter,
      keyCodes.arrowDown,
      keyCodes.arrowUp,
      keyCodes.arrowLeft,
      keyCodes.arrowRight,
      keyCodes.space,
      keyCodes.escape,
    ];

    if (searchNavigationBlacklist.includes(keyCode)) {
      return;
    }

    const isSearchingOptionWithNewKey =
      key.toLowerCase() !== options[hoveredOptionIndex]?.text[0].toLowerCase();

    const matchingOptionIndex = options.findIndex(
      (option, index) =>
        option.text[0].toLowerCase() === key.toLowerCase() &&
        (index > hoveredOptionIdx || isSearchingOptionWithNewKey),
    );

    if (matchingOptionIndex > -1) {
      handleOnSelectedOptionChange({
        optionValue: options[matchingOptionIndex].value,
        toggleList: false,
      });
      setHoveredOptionIndex(matchingOptionIndex);
    }
  };

  const handleHomeEndDown = (
    e: React.KeyboardEvent<HTMLSelectElement | HTMLInputElement>,
    optionIndex: number,
  ) => {
    e.preventDefault();
    setHoveredOptionIndex(optionIndex);
    onSelectedOptionChange(options[optionIndex].value);
  };

  const handleNativeSelectOnKeyDown: React.KeyboardEventHandler<
    HTMLSelectElement
  > = e => {
    const callOnChangeKeyCodes: Array<number> = [
      keyCodes.enter,
      keyCodes.arrowDown,
      keyCodes.arrowUp,
    ];
    const { keyCode, key } = e;

    const handleTabKeyDown = () => {
      const selectRef = ref as React.MutableRefObject<HTMLSelectElement>;
      selectRef?.current.focus();
    };
    const additionalActionFn = (
      hoveredOptionIdx: number,
      isHoveredOptionChanged: boolean,
    ) => {
      if (opened) {
        switch (keyCode) {
          case keyCodes.tab:
            handleTabKeyDown();
            return;
          case keyCodes.home:
            handleHomeEndDown(e, 0);
            return;
          case keyCodes.end:
            handleHomeEndDown(e, options.length - 1);
            return;
        }
      }

      if (callOnChangeKeyCodes.includes(keyCode) && isHoveredOptionChanged) {
        handleOnSelectedOptionChange({
          optionValue: options[hoveredOptionIdx].value,
          toggleList: false,
        });
      }

      handleSearchNavigationWithKey(key, hoveredOptionIdx, keyCode);
    };

    onKeyDown(e, additionalActionFn);
  };

  const handleNativeInputOnKeyDown: React.KeyboardEventHandler<
    HTMLInputElement
  > = e => {
    const { keyCode } = e;

    const additionalActionFn = (hoveredOptionIdx: number) => {
      if (opened && filteredOptions.length > 0) {
        switch (keyCode) {
          case keyCodes.home:
            handleHomeEndDown(e, 0);
            return;
          case keyCodes.end:
            handleHomeEndDown(e, filteredOptions.length - 1);
            return;
        }
      }

      if (keyCode === keyCodes.enter && filteredOptions[hoveredOptionIdx]) {
        handleOnSelectedOptionChange({
          optionValue: filteredOptions[hoveredOptionIdx].value,
          toggleList: false,
        });
        const i = options.findIndex(
          o => o.value === filteredOptions[hoveredOptionIdx].value,
        );
        setHoveredOptionIndex(i);
      }
    };

    onKeyDown(e, additionalActionFn);
  };

  const onInput: ChangeEventHandler<HTMLInputElement> = (
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    handleOnSelectedOptionChange({ optionValue: '', toggleList: false });
    onFilterQueryChange(e.target.value);
    setFilteredOptions(
      e.target.value ? filterOptionsByQuery(e.target.value, options) : options,
    );
    setHoveredOptionIndex(-1);
    if (!opened) {
      onOpenedChange(true);
    }
  };

  const handleOnSelectedOptionChange = ({
    optionValue,
    toggleList = true,
  }: {
    optionValue: string;
    toggleList?: boolean;
  }) => {
    onFilterQueryChange('');
    resetFilteredOptions();
    onSelectedOptionChange(optionValue);

    if (toggleList) {
      toggleDesignableList();

      const selectRef =
        ref as React.MutableRefObject<HTMLSelectElement | null> | null;

      if (isComboBoxSelectionFeedbackExperimentOpen) {
        selectRef?.current?.focus();
      }
    }
  };

  const onResizeCallback = React.useCallback(() => {
    if (designableListSourceElem) {
      setDesignableListWidth(calculateElemWidth(designableListSourceElem));
    }
  }, [designableListSourceElem]);
  useResizeObserver({
    elem: designableListSourceElem,
    callback: onResizeCallback,
  });

  const commonProps: Pick<
    ICustomSelectProps,
    | 'className'
    | 'styles'
    | 'id'
    | 'onFocus'
    | 'onBlur'
    | 'disabled'
    | 'required'
    | 'value'
    | 'options'
    | 'filteredOptions'
    | 'placeholder'
    | 'onClick'
    | 'ariaAttributes'
    | 'lang'
    | 'designableList'
    | 'autocomplete'
    | 'deviceType'
    | 'isValid'
    | 'shouldShowValidityIndication'
  > & {
    isOpen: boolean;
    hoveredOptionIndex: number;
  } = {
    className,
    styles,
    id,
    onFocus,
    onBlur,
    disabled,
    required,
    value,
    options,
    placeholder,
    isOpen: shouldOpenDesignableList,
    hoveredOptionIndex,
    onClick,
    ariaAttributes,
    filteredOptions,
    autocomplete,
    deviceType,
    isValid,
    shouldShowValidityIndication,
    lang,
  };

  return (
    <React.Fragment>
      {autocomplete || deviceType === 'Tablet' ? (
        <NativeInput
          {...commonProps}
          setWrapperRef={
            setDesignableListSourceElem as React.LegacyRef<HTMLDivElement>
          }
          ref={ref as Ref<HTMLInputElement>}
          onMouseDown={toggleDesignableList}
          onKeyDown={handleNativeInputOnKeyDown}
          filterQuery={filterQuery}
          onInput={onInput}
        />
      ) : (
        <NativeSelect
          {...commonProps}
          setWrapperRef={
            setDesignableListSourceElem as React.LegacyRef<HTMLDivElement>
          }
          designableList={true}
          ref={ref as Ref<HTMLSelectElement>}
          onMouseDown={handleNativeSelectOnMouseDown}
          onKeyDown={handleNativeSelectOnKeyDown}
        />
      )}
      {portalPoppersWrapper &&
        shouldOpenDesignableList &&
        createPortal(
          <div
            ref={setDesignableListTargetElem}
            className={portalClassName}
            style={{
              ...popperStyles.popper,
              ...{
                width: `${designableListWidth}px`,
                zIndex: isPopupPage
                  ? ('var(--portals-z-index)' as any)
                  : ('var(--above-all-z-index)' as any),
              },
            }}
            {...popperAttributes.popper}
          >
            <ComboBoxInputListModal
              id={id}
              value={value}
              setDesignableListElem={setDesignableListElem}
              onSelectedOptionChange={(optionValue, toggleList) =>
                handleOnSelectedOptionChange({ optionValue, toggleList })
              }
              options={filteredOptions}
              hoveredOptionIndex={hoveredOptionIndex}
              setHoveredOptionIndex={setHoveredOptionIndex}
              filteredOptions={filteredOptions}
              className={formatClassNames(semanticClassNames.list)}
              isComboBoxSelectionFeedbackExperimentOpen={
                isComboBoxSelectionFeedbackExperimentOpen
              }
            />
          </div>,
          portalPoppersWrapper,
        )}
    </React.Fragment>
  );
};

export default React.forwardRef(CustomSelect);
